import AppConstants from '@/common/constants/app.constants';
import { ErrorResponse } from '@/common/model/response/error-response.model';
import HTTPUtils from '@/common/utils/http.util';
import RouterUtils from '@/common/utils/router.util';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { VueConfiguration } from 'vue/types/vue';
import ErrorMessageUtils from './utils/error-message.util';
import FlashMessage from './components/flash-message.component';
import store from './store';
export default class HTTPClient {
  private static ignoredCommonSuccessHandlerURLs: Array<string> = [AppConstants.LINK_INDUCTION_DEVICE_URL];

  private static ignoredCommonErrorHandlerURLs: Array<string> = [AppConstants.GET_INDUCTION_STATUS_URL];

  // Axios instance to send requests to REST API
  private static instance: HTTPClient;

  private onError: Function | null;

  private onSuccess: Function | null;

  // Axios instance to send requests to REST API and show alert success/error message
  public HTTP!: AxiosInstance;

  // Axios instance to send requests to REST API and delay showing message
  public HTTP_WITH_DELAYED_MSG!: AxiosInstance;

  // Axios instance to send requests to REST API and delay showing success message and don't delay showing error message
  public HTTP_WITH_DELAYED_SUCCESS_MSG!: AxiosInstance;

  // Axios instance to send requests to REST API without showing success/error message
  public HTTP_WITHOUT_MSG!: AxiosInstance;

  // Axios instance to call login
  public httpLogin!: AxiosInstance;

  // Axios instance to call users api
  public httpUsers!: AxiosInstance;

  private constructor(
    config: VueConfiguration | null = null,
    onSuccess: Function | null = null,
    onError: Function | null = null
  ) {
    this.onSuccess = onSuccess;
    this.onError = onError;
    // Axios instance to send requests to REST API
    this.HTTP = this.initAxiosInstance(AppConstants.API_URL, true, onSuccess, onError, false, false);

    // Axios instance to send requests to REST API and delay showing message
    this.HTTP_WITH_DELAYED_MSG = this.initAxiosInstance(AppConstants.API_URL, true, onSuccess, onError, true, false);
    this.HTTP_WITHOUT_MSG = this.initAxiosInstance(AppConstants.API_URL, true, null, null, false, false);

    // Axios instance to call login
    this.httpLogin = this.initAxiosInstance(
      JSON.parse(JSON.stringify(config)).USERS_BASE_URL,
      false,
      null,
      null,
      false,
      false
    );
    // Axios instance to call users api
    this.httpUsers = this.initAxiosInstance(
      JSON.parse(JSON.stringify(config)).USERS_BASE_URL,
      true,
      onSuccess,
      onError,
      false,
      false
    );
    // Axios instance to send requests to REST API and delay showing success message and don't delay showing error message
    this.HTTP_WITH_DELAYED_SUCCESS_MSG = this.initAxiosInstance(
      AppConstants.API_URL,
      true,
      onSuccess,
      onError,
      false,
      true
    );
  }

  public initAxiosInstance(
    baseURL: string,
    initToken: boolean,
    _onSuccess: Function | null = null,
    _onError: Function | null = null,
    delayedShowingMsg = false,
    delayedShowingSuccessMsg = false
  ) {
    const instance = axios.create({
      baseURL
    });

    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    const axiosDefaultTimeout = config && config['AXIOS_DEFAULT_TIMEOUT'];
    instance.interceptors.request.use(function (config) {
      instance.defaults.timeout = axiosDefaultTimeout;
      if (initToken) {
        const token = localStorage.getItem(AppConstants.STORAGE_TOKEN);
        if (token) {
          config.headers.Authorization = 'Bearer ' + token;
        }
      }
      // Add 3D sorter work mode in header
      config.headers.workMode = AppConstants.WORK_MODE_T_SORT_3D;
      return config;
    });

    instance.interceptors.response.use(
      async (response: AxiosResponse) => {
        if (navigator.onLine) {
          await FlashMessage.getInstance().hideDisconnectMessage();
        }
        // Check to display 401 error
        if (AppConstants.API_URL === baseURL && HTTPUtils.is401Error(response)) {
          // return this.showError(
          //   'Your login session is invalid/expired or your account is blocked by admin user',
          //   response
          // );
          await FlashMessage.getInstance().showErrorMessage(response?.data?.errMsg, true);
          RouterUtils.goTo(AppConstants.LOGIN_ROUTER_PATH);
          throw new Error('401');
        }

        // Check to display 403 error
        if (AppConstants.API_URL === baseURL && HTTPUtils.isForbiddenResponse(response)) {
          // this.showError(`You don't have permission to access ${response.config.url}`, response);
          RouterUtils.goTo('/');
          throw new Error('403');
        }

        // 応答データがnullの場合はエラーをスローします
        if (!response?.data) {
          throw new Error();
        }

        // Do not display success message if msg2 property in response is empty or the current request URL is included in the ignore URL list
        const ignoreSuccessURL = HTTPClient.ignoredCommonSuccessHandlerURLs.includes(response?.config?.url as string);
        if (response?.data?.msg2 && !ignoreSuccessURL && this.onSuccess) {
          this.onSuccess(response?.data?.msg2, delayedShowingMsg || delayedShowingSuccessMsg);
        }
        return response;
      },
      async (error: AxiosError<ErrorResponse>) => {
        if (navigator.onLine) {
          await FlashMessage.getInstance().hideDisconnectMessage();
        }

        // 現在のリクエスト URL が無視 URL リストに含まれている場合、エラーを処理しません
        const ignoreErrorURL = HTTPClient.ignoredCommonErrorHandlerURLs.includes(error?.config?.url as string);
        if (ignoreErrorURL) {
          return Promise.reject(error);
        }

        const showDisconnectMsgFlag = await ErrorMessageUtils.checkToShowDisconnectMsg(error, true);
        if (showDisconnectMsgFlag) {
          throw error;
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        const authMode = config && config['AUTH_MODE'];
        if (authMode && initToken && HTTPUtils.isExpiredTokenError(error)) {
          store.dispatch('Auth/logout', { root: true });
          const { data } = error?.response as AxiosResponse<ErrorResponse>;
          await FlashMessage.getInstance().showErrorMessage(data?.errMsg, true);
          RouterUtils.goTo(AppConstants.LOGIN_ROUTER_PATH);
          return Promise.reject(error);
        }

        // Display default error message if msg2 and errMsg are unavailable
        const errorResponse: ErrorResponse | undefined = error.response?.data;
        if (!errorResponse || (!errorResponse?.errMap?.msg2?.[0] && !errorResponse?.errMsg)) {
          return this.handleShowErrorMessage('リクエストエラー', delayedShowingMsg, error);
        }

        // Display msg2 if it is available
        const msg2 = errorResponse?.errMap?.msg2?.[0];
        if (msg2) {
          let msg1 = errorResponse.errMap?.msg1?.[0];
          if (msg1) {
            msg1 = '[' + msg1 + ']';
          }
          return this.handleShowErrorMessage([msg1, msg2].join(' ').trim(), delayedShowingMsg, error);
        }

        // Display errMsg if it is available
        const errMsg = errorResponse?.errMsg;
        if (errMsg) {
          return this.handleShowErrorMessage(errMsg, delayedShowingMsg, error);
        }

        // Throw other error
        return Promise.reject(error);
      }
    );

    return instance;
  }

  static convertAxiosResponseToResponse<T>(response: AxiosResponse<T>) {
    if (!response || response.data === undefined || response.data === null) {
      console.error(response);
      throw new Error('レスポンスメッセージエラー');
    }
    return response.data;
  }

  public static getInstance(
    config: VueConfiguration | null = null,
    onSuccess: Function | null = null,
    onError: Function | null = null
  ) {
    if (!this.instance) {
      if (!config || !onSuccess || !onError) {
        throw new Error('HttpClient must be created via main.ts file instead');
      }
      this.instance = new HTTPClient(config, onSuccess, onError);
    }

    return this.instance;
  }

  private handleShowErrorMessage(errorMessage: string, delayedShowingMsg: boolean, error: AxiosError<ErrorResponse>) {
    if (this.onError) {
      this.onError(errorMessage, delayedShowingMsg);
    }
    return Promise.reject(error);
  }
}
